import React from 'react';
import { Process, PMNode } from '../../types';
import type { Segment } from '../../migrations/legacyEdge';
import Thread from './Thread';
import { segmentSemanticStyle } from '../../utils/mapping/semanticStyles';
import { buildEdgeMap, resolveProcessEdges } from '../../utils/processEdges';

interface ThreadLayerProps {
  processes:      Process[];
  edges:          Segment[];
  nodes:          Record<string, PMNode>;
  onSegmentClick: (procId: number, segIdx: number) => void;
  onEndpointMouseDown: (
    e: React.MouseEvent<SVGCircleElement>,
    procId: number,
    segIdx: number,
    endpoint: 'from' | 'to',
    point: { x: number; y: number }
  ) => void;
  draggingEndpointKey?: string | null;
  dragPreview?: {
    procId: number;
    segIdx: number;
    endpoint: 'from' | 'to';
    x: number;
    y: number;
  } | null;
  hoveredSegmentKey?: string | null;
  onSegmentHover?: (procId: number, segIdx: number, hovered: boolean) => void;
  playbackSegmentState?: Record<string, boolean> | null;
}

const ThreadLayer: React.FC<ThreadLayerProps> = ({
  processes,
  edges,
  nodes,
  onSegmentClick,
  onEndpointMouseDown,
  draggingEndpointKey = null,
  dragPreview = null,
  hoveredSegmentKey = null,
  onSegmentHover,
  playbackSegmentState = null,
}) => (
  <g id="layer-threads">
    {(() => {
      const visibleProcesses = processes.filter(proc => proc.visible !== false);
      const edgeMap = buildEdgeMap(edges);
      const pairTotals = new Map<string, number>();

      visibleProcesses.forEach(proc => {
        resolveProcessEdges(proc, edgeMap).forEach(seg => {
          const from = nodes[seg.from];
          const to = nodes[seg.to];
          if (!from || !to) return;
          const pairKey = [seg.from, seg.to].sort().join('::');
          pairTotals.set(pairKey, (pairTotals.get(pairKey) ?? 0) + 1);
        });
      });

      const pairSeen = new Map<string, number>();

      return processes.map((proc, pi) => {
        if (proc.visible === false) return null;
        return resolveProcessEdges(proc, edgeMap).map((seg, si) => {
          const from = nodes[seg.from];
          const to   = nodes[seg.to];
          if (!from || !to) return null;
          const isPreviewTarget = dragPreview?.procId === proc.id && dragPreview?.segIdx === si;
          const fromX = isPreviewTarget && dragPreview?.endpoint === 'from' ? dragPreview.x : from.x;
          const fromY = isPreviewTarget && dragPreview?.endpoint === 'from' ? dragPreview.y : from.y;
          const toX = isPreviewTarget && dragPreview?.endpoint === 'to' ? dragPreview.x : to.x;
          const toY = isPreviewTarget && dragPreview?.endpoint === 'to' ? dragPreview.y : to.y;

          const pairKey = [seg.from, seg.to].sort().join('::');
          const pairIdx = pairSeen.get(pairKey) ?? 0;
          pairSeen.set(pairKey, pairIdx + 1);
          const pairTotal = pairTotals.get(pairKey) ?? 1;

          return (
            <Thread
              key={`${proc.id}-${si}`}
              seg={seg}
              segIdx={si}
              pairIdx={pairIdx}
              pairTotal={pairTotal}
              proc={proc}
              procIndex={pi}
              totalProcs={processes.length}
              fromX={fromX} fromY={fromY}
              toX={toX}     toY={toY}
              onClick={() => onSegmentClick(proc.id, si)}
              onEndpointMouseDown={(e, endpoint, point) => onEndpointMouseDown(e, proc.id, si, endpoint, point)}
              isEndpointDragActive={draggingEndpointKey === `${proc.id}:${si}`}
              isHovered={hoveredSegmentKey === `${proc.id}:${si}`}
              onHoverChange={(hovered) => onSegmentHover?.(proc.id, si, hovered)}
              semanticStyle={segmentSemanticStyle(seg)}
              isPlaybackVisible={playbackSegmentState ? Boolean(playbackSegmentState[`${proc.id}:${si}`]) : true}
            />
          );
        });
      });
    })()}
  </g>
);

export default ThreadLayer;
